import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  WEBSOCKET_BASE_URL,
  WebSocketService,
} from '@qtek/core/websockets-core';
import { HealthcheckCoreService } from '@qtek/libs/healthcheck-core';
import { MetaCoreFeature } from '@qtek/libs/meta-core';
import {
  AuthActions,
  AuthFeature,
  AuthState,
  MenuStore,
  UserStore,
} from '@qtek/libs/store';
import { WsErrorHandlerService } from '@qtek/libs/ws-error-handler';
import { isNonNullable } from '@qtek/shared/utils';
import { Observable, of } from 'rxjs';
import { filter, map, mergeMap, take, tap } from 'rxjs/operators';

@Injectable()
export class AuthGuard implements CanActivate {
  private store = inject<Store<AuthState>>(Store);
  private menuStore = inject(MenuStore);
  private actions$ = inject(Actions);
  private wsService = inject(WebSocketService);
  private healthCheckCoreService = inject(HealthcheckCoreService);
  private websocketBaseUrl = inject(WEBSOCKET_BASE_URL);
  private wsErrorHandler = inject(WsErrorHandlerService);
  private userStore = inject(UserStore);

  connectToWebsocket() {
    this.store.dispatch(AuthActions.getWSInitToken());

    return this.actions$.pipe(
      ofType(
        AuthActions.getWSInitTokenSuccess,
        AuthActions.getWSInitTokenError
      ),
      take(1),
      mergeMap(action => {
        if (action.type === AuthActions.getWSInitTokenError.type) {
          return of(null);
        }
        return of(action);
      }),
      mergeMap(() => this.store.select(AuthFeature.selectWsInitToken)),
      tap(wsTkn => {
        // reconnect ws with tkn
        this.wsService.initWebsocketConnection(
          this.store
            .select(MetaCoreFeature.selectWsPingPong)
            .pipe(filter(isNonNullable)),
          this.wsErrorHandler.errorsHandler.bind(this.wsErrorHandler),
          { wsTkn }
        );
        this.healthCheckCoreService.registerCheckHealthSubscription(
          this.store
            .select(MetaCoreFeature.selectWsPingPong)
            .pipe(filter(isNonNullable)),
          of('/api/v1/service/meta')
        );
      })
    );
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const isImpersonate =
      state.url.startsWith('/orgs/impersonate') || route.queryParamMap.has('t');

    if (isImpersonate) {
      return of(true);
    }

    /** Fetch app navigation. */
    this.menuStore.loadMain();

    return this.userStore
      .getCurrentUser({ skipRevalidateErrorHandling: true })
      .pipe(
        mergeMap(success => {
          if (!success) {
            const returnUrl = state.url !== '/' ? state.url : '';
            this.store.dispatch(
              AuthActions.logout({
                queryParams: returnUrl ? { returnUrl } : null,
              })
            );
            return of(false);
          }
          const initWs = this.websocketBaseUrl
            ? this.connectToWebsocket()
            : of(null);
          return initWs.pipe(map(() => true));
        }),
        mergeMap(canActivate => {
          if (!canActivate) return of(false);
          return this.wsService.init$.pipe(
            take(1),
            map(() => true)
          );
        })
      );
  }
}
